import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { CharacterWithStats, Roll } from '@shared/types'
import { formatCopper } from '@shared/rules/coins'
import { ApiError, api } from '@/lib/api'
import { Button, ErrorNote, Panel, SectionHeading } from '@/components/ui'
import { RollResultCard } from '@/components/dice/RollResultCard'
import { PartyStrip } from './PartyStrip'
import { QuickReference } from './QuickReference'
import { cn } from '@/lib/cn'

const POLL_MS = 4000

/**
 * The screen a DM keeps open while running a session.
 *
 * Everything here was previously behind a tab: party health, secret checks,
 * conditions, the roll feed, and somewhere to write down what happened. The
 * point is that during play there is no navigation at all.
 */
export function DmDashboard({ campaignId, campaignName }: { campaignId: string; campaignName: string }) {
  const navigate = useNavigate()
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getDashboard>> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [noteSaved, setNoteSaved] = useState(false)
  const paused = useRef(false)

  const load = useCallback(async () => {
    try {
      setData(await api.getDashboard(campaignId))
      setError(null)
    } catch (err) {
      if (err instanceof ApiError) setError(err.message)
    }
  }, [campaignId])

  useEffect(() => {
    void load()
    const timer = setInterval(() => { if (!paused.current && !document.hidden) void load() }, POLL_MS)
    return () => clearInterval(timer)
  }, [load])

  /**
   * Applies a change immediately and reconciles from the server afterwards, so
   * the bar moves the instant the DM clicks rather than on the next poll.
   */
  async function patchCharacter(id: string, patch: Record<string, unknown>) {
    paused.current = true
    setData((current) => current && {
      ...current,
      characters: current.characters.map((c) => (c.id === id ? { ...c, ...patch } as CharacterWithStats : c)),
    })
    try {
      await api.updateCharacter(id, patch)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that')
    } finally {
      paused.current = false
      void load()
    }
  }

  async function rollFor(character: CharacterWithStats, formula: string, context: string) {
    try {
      await api.createRoll(campaignId, { formula, context, characterId: character.id, private: true })
      void load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not roll')
    }
  }

  async function saveNote() {
    const text = note.trim()
    if (!text) return
    setSavingNote(true)
    try {
      await api.createStory(campaignId, {
        title: `Session note — ${new Date().toLocaleDateString()}`,
        body: text,
        visibility: 'dm_only',
        sessionDate: new Date().toISOString().slice(0, 10),
        tags: ['session-note'],
      })
      setNote('')
      setNoteSaved(true)
      setTimeout(() => setNoteSaved(false), 3000)
      void load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the note')
    } finally {
      setSavingNote(false)
    }
  }

  if (!data) {
    return <Panel className="p-10 text-center text-sm text-ink-500">Loading the table…</Panel>
  }

  const bloodied = data.characters.filter((c) => c.hpCurrent > 0 && c.hpCurrent <= c.hpMax / 2).length
  const down = data.characters.filter((c) => c.hpCurrent <= 0).length

  return (
    <div className="space-y-8">
      {error && <ErrorNote>{error}</ErrorNote>}

      {/* At-a-glance state of the table */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Party" value={data.characters.length} />
        <Stat label="Bloodied" value={bloodied} tone={bloodied > 0 ? 'gold' : undefined} />
        <Stat label="Down" value={down} tone={down > 0 ? 'blood' : undefined} />
        <Stat label="Party funds" value={formatCopper(data.balanceCp)} small />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-8">
          <section>
            <SectionHeading
              action={
                <span className="text-[11px] text-ink-500">
                  Enter damages · Shift+Enter heals · Per/Ins/Sav roll in secret
                </span>
              }
            >
              The party
            </SectionHeading>
            <PartyStrip characters={data.characters} onPatch={patchCharacter} onRoll={rollFor} />
          </section>

          <section>
            <SectionHeading
              action={
                <Button size="sm" variant="ghost" onClick={() => navigate(`/campaigns/${campaignId}?tab=story`)}>
                  All entries →
                </Button>
              }
            >
              Session notes
            </SectionHeading>
            <Panel className="p-3">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void saveNote() }}
                placeholder="What just happened? ⌘↵ to save it to the story log as a DM-only entry."
                className="min-h-24 w-full resize-y rounded-xl border border-line bg-ink-900/60 px-3.5 py-2.5 text-sm leading-relaxed text-ink-100 placeholder:text-ink-500 focus:border-gold-500/60 focus:outline-none"
              />
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[11px] text-ink-500">
                  {data.lastSession ? `Last entry: ${data.lastSession.title}` : 'No entries yet'}
                </span>
                <div className="flex items-center gap-2">
                  {noteSaved && <span className="text-[11px] text-moss-500">Saved</span>}
                  <Button size="sm" variant="primary" busy={savingNote} disabled={!note.trim()} onClick={saveNote}>
                    Save note
                  </Button>
                </div>
              </div>
            </Panel>
          </section>
        </div>

        <div className="space-y-8">
          <section>
            <SectionHeading
              action={
                <span className="flex items-center gap-1.5 text-[11px] text-ink-500">
                  <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-moss-500" /> live
                </span>
              }
            >
              Rolls
            </SectionHeading>
            {data.rolls.length === 0 ? (
              <Panel className="p-6 text-center text-sm text-ink-500">Nothing rolled yet.</Panel>
            ) : (
              <div className="max-h-[26rem] space-y-2 overflow-y-auto pr-1">
                {(data.rolls as Roll[]).slice(0, 10).map((roll) => (
                  <RollResultCard key={roll.id} roll={roll} />
                ))}
              </div>
            )}
          </section>

          <section>
            <SectionHeading>Quick reference</SectionHeading>
            <Panel className="p-3">
              <QuickReference />
            </Panel>
          </section>
        </div>
      </div>

      <p className="text-center text-[11px] text-ink-600">
        {campaignName} · updates every few seconds
      </p>
    </div>
  )
}

function Stat({
  label, value, tone, small,
}: {
  label: string
  value: string | number
  tone?: 'gold' | 'blood'
  small?: boolean
}) {
  return (
    <div className="panel p-4">
      <p className={cn(
        'font-mono tabular-nums',
        small ? 'text-lg' : 'text-3xl',
        tone === 'blood' ? 'text-blood-500' : tone === 'gold' ? 'text-gold-400' : 'text-ink-100',
      )}>
        {value}
      </p>
      <p className="mt-1 text-label">{label}</p>
    </div>
  )
}
